import React from 'react';
import Axios from 'axios';
import './DropsPage.scss';

const STORAGE_KEY = 'noizDropsLinkPending';
const MAX_AGE_MS = 15 * 60 * 1000;
const STATE_KEY = 'noizDropsOauthState';

function readPending() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.code || !parsed.createdAt) return null;
    if (Date.now() - parsed.createdAt > MAX_AGE_MS) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

function writePending(payload) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (e) {
    // ignore
  }
}

function clearPending() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // ignore
  }
}

class DropsLinkPage extends React.Component {
  constructor(props) {
    super(props);
    this.props = props;
    this.state = {
      status: 'idle',
      message: null,
      details: null,
    };
  }

  componentDidMount() {
    this.bootstrap();
  }

  getToken = () => {
    return this.props?.auth?.token || null;
  };

  bootstrap = async () => {
    const routerQuery = this.props?.router?.location?.query || {};
    const parsedQuery = (() => {
      try {
        const u = new URL(window.location.href);
        const obj = {};
        for (const [k, v] of u.searchParams.entries()) obj[k] = v;
        return obj;
      } catch (e) {
        return {};
      }
    })();
    const query = { ...parsedQuery, ...routerQuery };
    const code = (query.code || '').toString().trim();
    const returnedState = (query.state || '').toString().trim();
    const error = (query.error || '').toString().trim();
    const errorDescription = (query.error_description || '').toString().trim();
    const scope = (query.scope || '').toString().trim();

    if (error) {
      this.setState({
        status: 'error',
        message: `Twitch linking failed: ${errorDescription || error}`,
        details: { error, errorDescription },
      });
      return;
    }

    if (code) {
      writePending({ code, scope, createdAt: Date.now() });
    }

    // CSRF protection: validate state if we have an expected one.
    if (code && returnedState) {
      try {
        const expected = sessionStorage.getItem(STATE_KEY);
        if (expected && expected !== returnedState) {
          this.setState({
            status: 'error',
            message: 'State mismatch in OAuth callback. Please retry linking.',
            details: { expected, returnedState },
          });
          return;
        }
      } catch (e) {
        // ignore
      }
    }

    const pending = readPending();
    if (!pending?.code) {
      this.setState({
        status: 'idle',
        message: 'To link your account, open /drops/link/start to begin.',
      });
      return;
    }

    const token = this.getToken();
    if (!token) {
      window.location.href = `/sign-in?redirectto=${encodeURIComponent('/drops/link')}`;
      return;
    }

    await this.completeLink(pending.code);
  };

  completeLink = async (code) => {
    try {
      this.setState({ status: 'working', message: 'Linking…', details: null });
      const token = this.getToken();

      const routerQuery = this.props?.router?.location?.query || {};
      const parsedQuery = (() => {
        try {
          const u = new URL(window.location.href);
          const obj = {};
          for (const [k, v] of u.searchParams.entries()) obj[k] = v;
          return obj;
        } catch (e) {
          return {};
        }
      })();
      const query = { ...parsedQuery, ...routerQuery };

      const redirectUri = (() => {
        try { return sessionStorage.getItem('noizDropsLinkRedirectUri'); } catch (e) { return null; }
      })();
      const effectiveRedirectUri = redirectUri || `${window.location.origin}/drops/link`;

      // Helpful for debugging when Twitch inventory still shows "Connect".
      // eslint-disable-next-line no-console
      console.log('[DropsLink] href=', window.location.href);
      // eslint-disable-next-line no-console
      console.log('[DropsLink] query=', JSON.stringify(query || {}, null, 2));
      // eslint-disable-next-line no-console
      console.log('[DropsLink] redirectUri=', effectiveRedirectUri);

      const resp = await Axios.post(
        '/api/v1/user/drops/link/complete',
        { code, redirectUri: effectiveRedirectUri },
        { headers: { Authorization: `JWT ${token}` } }
      );
      clearPending();
      try { sessionStorage.removeItem(STATE_KEY); } catch (e) { /* ignore */ }
      this.setState({
        status: 'done',
        message: 'Linked successfully.',
        details: {
          ...(resp?.data?.data || {}),
          redirectUri: effectiveRedirectUri,
          next: 'Return to Twitch Inventory and refresh the page.',
        },
      });
    } catch (e) {
      this.setState({
        status: 'error',
        message: e?.response?.data?.message || e.message || 'Linking failed',
        details: e?.response?.data || null,
      });
    }
  };

  render() {
    const { status, message, details } = this.state;

    return (
      <section className="DropsPage">
        <header className="DropsPage__header">
          <h1>Twitch Drops – Account Linking</h1>
        </header>

        <section className="DropsPage__card">
          {message ? <div className={status === 'error' ? 'DropsPage__error' : 'DropsPage__hint'}>{message}</div> : null}
          {status === 'working' ? <div className="DropsPage__loading">Working…</div> : null}

          <div className="DropsPage__actions">
            <a className="site-button" href="/drops">
              Back to Drops
            </a>
            <a
              className="site-button"
              href="https://www.twitch.tv/drops/inventory"
            >
              Back to Twitch
            </a>
          </div>

          {details ? <pre className="DropsPage__debugOutput">{JSON.stringify(details, null, 2)}</pre> : null}
        </section>
      </section>
    );
  }
}

export default DropsLinkPage;
